import React, { useEffect, useState } from "react";
import { Receipt, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { fmtDate, money } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty, interimDue } from "@/pages/shared";
import StaffPicker from "@/components/StaffPicker";
import MeterField from "@/components/MeterField";
import { withUid } from "@/lib/uid";
import InvoiceDoneDialog from "@/components/InvoiceDoneDialog";
import SettlementExtras from "@/components/SettlementExtras";
import OutstandingCard from "@/components/OutstandingCard";
import { useCamp } from "@/context/SettingsContext";

const today = () => new Date().toISOString().slice(0, 10);
const endOfLastMonth = () => {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0)).toISOString().slice(0, 10);
};

export default function Afrekening() {
  const { t } = useLang();
  const { camp } = useCamp();
  const interimDays = camp?.interim_reminder_days || 30;
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [until, setUntil] = useState(today());
  const [items, setItems] = useState([]);
  const [taxRate, setTaxRate] = useState(21);
  const [busy, setBusy] = useState(false);
  const [staff, setStaff] = useState(null);
  const [pitches, setPitches] = useState([]);
  const [meterNow, setMeterNow] = useState("");
  const [done, setDone] = useState(null);
  const [extras, setExtras] = useState(null);
  const [incl, setIncl] = useState([]);

  const printInvoice = (inv) => window.open(`/print-factuur?invoice=${inv.id}`, "_blank", "noopener");

  const mailInvoice = async (inv) => {
    try {
      await api.post(`/invoices/${inv.id}/send`);
      toast.success(t("invoice_mailed"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const load = async () => {
    try {
      const [r, p] = await Promise.all([api.get("/reservations?filter=checked_in"), api.get("/pitches")]);
      setRows(r.data); setPitches(p.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  // Lijnen opbouwen tot de opgegeven datum op basis van de tarievenmodule
  const build = async (r, u, meter = meterNow, ex = extras, inclIds = incl) => {
    const stop = u < r.arrival ? r.arrival : u;
    let base = [];
    try {
      const mq = meter === "" || meter == null ? "" : `&meter_now=${Number(meter)}`;
      const alsoQ = inclIds?.length ? `&also=${inclIds.join(",")}` : "";
      const { data: q } = await api.get(`/tariffs/quote?reservation_id=${r.id}&until=${stop}${mq}${alsoQ}`);
      base = q.lines.map((l) => ({ description: l.description, qty: l.qty, unit_price: l.unit_price, kind: l.kind }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    if (!base.length) toast.info(t("tariff_no_tariffs"));
    try {
      const { data: ch } = await api.get(`/rentals/charges?reservation_id=${r.id}&until=${stop}&lang=${r.language || "nl"}`);
      ch.lines.forEach((l) => base.push({ description: l.description, qty: l.qty, unit_price: l.unit_price, kind: "rental" }));
    } catch (e) { console.warn("Verhuurlijnen ophalen mislukt", e?.message); }
    (ex?.sales || []).forEach((s) => base.push({
      description: `${s.description}${s.number ? ` (${s.number})` : ""}`, qty: 1, unit_price: s.total, kind: "shop",
    }));
    setItems(withUid(base));
  };

  const open = async (r) => {
    const u = today();
    let ex = { parallel: [], sales: [], open_invoices: [] };
    try { ex = (await api.get(`/reservations/${r.id}/settlement-extras?until=${u}`)).data; }
    catch (e) { console.warn("Extra afrekengegevens niet opgehaald", e?.message); }
    const auto = ex.parallel.filter((p) => p.same_end).map((p) => p.reservation_id);
    setExtras(ex); setIncl(auto);
    setSel(r); setUntil(u); setTaxRate(21); setMeterNow("");
    await build(r, u, "", ex, auto);
  };

  const toggleIncl = async (id) => {
    const next = incl.includes(id) ? incl.filter((x) => x !== id) : [...incl, id];
    setIncl(next);
    if (sel) await build(sel, until, meterNow, extras, next);
  };

  const setUntilAndBuild = async (u) => { setUntil(u); if (sel) await build(sel, u, meterNow, extras, incl); };

  const subtotal = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const tax = subtotal * (Number(taxRate) || 0) / 100;

  const create = async () => {
    setBusy(true);
    try {
      const { data: inv } = await api.post("/invoices", {
        guest_id: sel.guest_id, language: sel.language, tax_rate: Number(taxRate), currency: "EUR", staff_id: staff?.id || null,
        sale_ids: (extras?.sales || []).map((s) => s.id),
        close_reservation_ids: incl.filter((id) => !(extras?.parallel || []).some((p) => p.reservation_id === id && p.same_end)),
        until,
        interim_reservation_id: sel.id,
        items: items.filter((i) => i.description).map((i) => ({ description: i.description, qty: Number(i.qty), unit_price: Number(i.unit_price), kind: i.kind || "other" })),
      });
      await api.post("/rentals/mark-billed", { reservation_id: sel.id, until }).catch(() => {});
      const pitch = pitches.find((p) => p.label === sel.pitch_label);
      if (pitch && meterNow !== "") {
        await api.post(`/pitches/${pitch.id}/meter`, { value: Number(meterNow), kind: "interim", reservation_id: sel.id });
      }
      const mailTo = sel.booker?.email || "";
      setSel(null); toast.success(t("created"));
      setDone({ inv, email: mailTo });
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Panel title={t("checked_in_guests")} testid="interim-panel">
      {rows.length === 0 ? <Empty /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("guest")}</TableHead>
              <TableHead>{t("pitch")}</TableHead>
              <TableHead>{t("arrival")}</TableHead>
              <TableHead>{t("departure")}</TableHead>
              <TableHead>{t("persons")}</TableHead>
              <TableHead>{t("interim_last")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...rows].sort((a, b) => (interimDue(b, interimDays) ? 1 : 0) - (interimDue(a, interimDays) ? 1 : 0)).map((r) => {
              const due = interimDue(r, interimDays);
              return (
              <TableRow key={r.id} data-testid={`interim-row-${r.id}`}>
                <TableCell className="font-medium">{r.guest_name}</TableCell>
                <TableCell><span className="font-semibold text-primary">{r.pitch_label}</span></TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(r.arrival)}</TableCell>
                <TableCell className="text-muted-foreground" data-testid={`interim-departure-${r.id}`}>{fmtDate(r.departure)}</TableCell>
                <TableCell>{r.persons}</TableCell>
                <TableCell className="text-xs">
                  {due ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800" data-testid={`interim-due-${r.id}`}>
                      <BellRing className="h-3 w-3" />{t("interim_due")}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{r.last_interim_until ? fmtDate(r.last_interim_until) : "—"}</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant={due ? "default" : "outline"} onClick={() => open(r)} data-testid={`interim-invoice-${r.id}`}>
                    <Receipt className="h-4 w-4 mr-1" />{t("create_invoice_for")}
                  </Button>
                </TableCell>
              </TableRow>
            );})}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="interim-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Receipt className="h-4 w-4" />{t("interim_invoice")} — {sel?.guest_name} · {sel?.pitch_label}
            </DialogTitle>
          </DialogHeader>
          <StaffPicker staff={staff} setStaff={setStaff} />
          <SettlementExtras extras={extras} incl={incl} onToggle={toggleIncl} />
          <OutstandingCard guestId={sel?.guest_id} />
          <MeterField meterStart={sel?.meter_start} value={meterNow} onChange={setMeterNow}
            lastKnown={pitches.find((p) => p.label === sel?.pitch_label)?.meter_last}
            onRecalc={() => sel && build(sel, until, meterNow)} />
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs">{t("bill_until")}</Label>
              <Input type="date" value={until} min={sel?.arrival} max={sel?.departure}
                onChange={(e) => setUntilAndBuild(e.target.value)} data-testid="interim-until" />
            </div>
            <Button variant="outline" size="sm" onClick={() => setUntilAndBuild(endOfLastMonth())} data-testid="interim-last-month">{t("end_last_month")}</Button>
            <Button variant="outline" size="sm" onClick={() => setUntilAndBuild(today())} data-testid="interim-today">{t("view_today")}</Button>
          </div>
          <div className="space-y-2 max-h-[40vh] overflow-auto cb-scroll pr-1">
            {items.map((it, i) => (
              <div key={it._uid} className="grid grid-cols-[1fr_70px_90px_90px] gap-2 items-center" data-testid={`interim-line-${i}`}>
                <Input value={it.description} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
                <Input type="number" value={it.qty} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} />
                <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x))} />
                <span className="text-right tabular-nums text-sm">{money((Number(it.qty) || 0) * (Number(it.unit_price) || 0))}</span>
              </div>
            ))}
          </div>
          <div className="space-y-1 text-sm border-t border-border pt-2">
            <div className="flex justify-between"><span className="text-muted-foreground">{t("subtotal")}</span><span className="tabular-nums" data-testid="interim-subtotal">{money(subtotal)}</span></div>
            <div className="flex justify-between">
              <span className="text-muted-foreground flex items-center gap-1">{t("vat")} <Input type="number" className="h-7 w-16 text-xs" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="interim-tax" />%</span>
              <span className="tabular-nums">{money(tax)}</span>
            </div>
            <div className="flex justify-between font-display font-extrabold text-lg text-primary"><span>{t("total")}</span><span className="tabular-nums" data-testid="interim-total">{money(subtotal + tax)}</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSel(null)}>{t("cancel")}</Button>
            <Button onClick={create} disabled={busy || items.length === 0} data-testid="interim-confirm">
              <Receipt className="h-4 w-4 mr-1" />{t("create_invoice_for")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Een afrekening is pas afgewerkt zodra de factuur afgedrukt of gemaild is */}
      <InvoiceDoneDialog
        open={!!done}
        number={done?.inv?.number}
        email={done?.email}
        onFinish={async ({ print, mail }) => {
          if (print) printInvoice(done.inv);
          if (mail) await mailInvoice(done.inv);
          setDone(null);
        }}
      />
    </Panel>
  );
}
